import React from 'react'

import { buildFullUrl, extractBasePath, toOgLocale } from './utils'

export function WebsiteHelmet({
  locale,
  canonicalUrl,
  title,
  description,
  ogImage,
  ogTitle,
  ogDescription,
  disableIndexation,
  additionalMetaTags = [],
  config,
  alternateHrefLangUrls,
}: {
  locale: string
  canonicalUrl: string
  title: string
  description: string
  ogImage: string
  ogTitle?: string
  ogDescription?: string
  disableIndexation?: boolean
  additionalMetaTags?: { content: string; name: string }[]
  config: {
    domain: string
    siteName: string
    locales: string[]
    defaultLocale: string
    favicons: {
      ico: string
      png16: string
      png32: string
      appleTouchIcon: string
    }
  }
  /** Custom alternate URLs per locale (for content with different slugs per language) */
  alternateHrefLangUrls?: Record<string, string>
}) {
  const { domain, siteName, locales, defaultLocale, favicons } = config

  const basePath = extractBasePath(canonicalUrl, locales)
  const fullCanonicalUrl = buildFullUrl(domain, basePath, locale, defaultLocale)

  const hreflangLinks = [
    ...locales.map((loc) => ({
      rel: 'alternate' as const,
      hrefLang: loc.toLowerCase(),
      href: alternateHrefLangUrls?.[loc]
        ? `${domain}${alternateHrefLangUrls[loc]}`
        : buildFullUrl(domain, basePath, loc, defaultLocale),
    })),
    {
      rel: 'alternate' as const,
      hrefLang: 'x-default',
      href: alternateHrefLangUrls?.[defaultLocale]
        ? `${domain}${alternateHrefLangUrls[defaultLocale]}`
        : buildFullUrl(domain, basePath, defaultLocale, defaultLocale),
    },
  ]

  // OG locale
  const ogLocale = toOgLocale(locale)
  const ogLocaleAlternates = locales
    .filter((loc) => loc !== locale)
    .map((loc) => toOgLocale(loc))

  return (
    <>
      <title>{title}</title>
      <link rel="canonical" href={fullCanonicalUrl} />
      {hreflangLinks.map((link) => (
        <link
          key={link.hrefLang}
          rel="alternate"
          hrefLang={link.hrefLang}
          href={link.href}
        />
      ))}
      <link rel="icon" href={favicons.ico} type="image/x-icon" />
      <link rel="icon" type="image/png" sizes="16x16" href={favicons.png16} />
      <link rel="icon" type="image/png" sizes="32x32" href={favicons.png32} />
      <link
        rel="apple-touch-icon"
        type="image/png"
        href={favicons.appleTouchIcon}
      />
      <meta property="og:site_name" content={siteName} />
      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {disableIndexation && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={ogLocale} />
      {ogLocaleAlternates.map((alt) => (
        <meta key={alt} property="og:locale:alternate" content={alt} />
      ))}

      {/* Twitter */}
      <meta content="summary_large_image" name="twitter:card" />
      <meta name="twitter:url" content={fullCanonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {additionalMetaTags.map((tag, index) => (
        <meta key={index} name={tag.name} content={tag.content} />
      ))}
    </>
  )
}
